import type { SupabaseClient } from '@supabase/supabase-js'
import type { Presupuesto, Database } from '@/types/database'
import { hoyEnArgentina } from '@/core/services/tareasRecurrentesService'

export interface FiltrosPresupuestos {
  estado?: 'borrador' | 'enviado' | 'aceptado' | 'rechazado'
  fechaDesde?: string
  fechaHasta?: string
  clienteNombre?: string
}

type PresupuestoInsert = Database['public']['Tables']['presupuestos']['Insert']
type PresupuestoUpdate = Database['public']['Tables']['presupuestos']['Update']

/**
 * Servicio para gestión de presupuestos.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * const presupuestos = await presupuestosService.listar(supabase!, filtros)
 */
export const presupuestosService = {
  async listar(client: SupabaseClient<Database>, filtros?: FiltrosPresupuestos): Promise<Presupuesto[]> {
    let query = client
      .from('presupuestos')
      .select('*')
      .order('fecha', { ascending: false })

    if (filtros?.estado) {
      query = query.eq('estado', filtros.estado)
    }
    if (filtros?.fechaDesde) {
      query = query.gte('fecha', filtros.fechaDesde)
    }
    if (filtros?.fechaHasta) {
      query = query.lte('fecha', filtros.fechaHasta)
    }
    if (filtros?.clienteNombre) {
      query = query.ilike('cliente_nombre', `%${filtros.clienteNombre}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data
  },

  async obtenerPorId(client: SupabaseClient<Database>, id: string): Promise<Presupuesto> {
    const { data, error } = await client
      .from('presupuestos')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  },

  async crear(client: SupabaseClient<Database>, presupuesto: PresupuestoInsert): Promise<Presupuesto> {
    const { data, error } = await client
      .from('presupuestos')
      .insert(presupuesto)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async actualizar(client: SupabaseClient<Database>, id: string, presupuesto: PresupuestoUpdate): Promise<Presupuesto> {
    const { data, error } = await client
      .from('presupuestos')
      .update(presupuesto)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    // RPC con RBAC: verifica rol del caller antes de eliminar
    const { error } = await client.rpc('eliminar_presupuesto_seguro', { p_id: id })
    if (error) throw error
  },

  async cambiarEstado(client: SupabaseClient<Database>, id: string, estado: Presupuesto['estado']): Promise<Presupuesto> {
    const { data, error } = await client
      .from('presupuestos')
      .update({ estado })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async duplicar(client: SupabaseClient<Database>, id: string): Promise<Presupuesto> {
    // Obtener presupuesto original
    const original = await this.obtenerPorId(client, id)
    const hoy = hoyEnArgentina()

    // Crear copia sin id (numero lo asigna trigger)
    const copia: PresupuestoInsert = {
      organizacion_id: original.organizacion_id,
      cliente_nombre: original.cliente_nombre,
      cliente_cuit: original.cliente_cuit,
      cliente_id: original.cliente_id,
      cliente_email: original.cliente_email,
      admin_nombre: original.admin_nombre,
      admin_cuit: original.admin_cuit,
      condiciones_pago: original.condiciones_pago,
      numero: 0, // Required field, trigger lo sobreescribe
      titulo: original.titulo,
      fecha_emision: hoy,
      fecha: hoy,
      validez_dias: original.validez_dias,
      items: original.items,
      subtotal: original.subtotal,
      descuento_porcentaje: original.descuento_porcentaje,
      iva_porcentaje: original.iva_porcentaje,
      total: original.total,
      notas: original.notas,
      estado: 'borrador',
      created_by: original.created_by,
    }

    return this.crear(client, copia)
  },

  async obtenerEstadisticas(client: SupabaseClient<Database>, organizacionId: string): Promise<{
    total: number
    borradores: number
    enviados: number
    aceptados: number
    rechazados: number
    montoTotal: number
    montoAceptado: number
  }> {
    // RPC no tipada en Database (no fue regenerada). Firma manual local hasta el próximo `supabase gen types`.
    const { data, error } = await (client.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>)(
      'get_estadisticas_presupuestos',
      { p_org_id: organizacionId },
    )
    if (error) throw error
    return data as { total: number; borradores: number; enviados: number; aceptados: number; rechazados: number; montoTotal: number; montoAceptado: number }
  },
}
